import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { colors, theme, setTheme } = useTheme();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to log out');
    }
  };

  return (
    <>
      {/* Sidebar */}
      <div 
        className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}
        style={{ 
          background: colors.card
        }}
      >
        <div className="sidebar-header">
          <img 
            src="/pix-logo.png" 
            alt="Pixano Logo" 
            style={{ 
              height: isOpen ? '52px' : '52px', 
              width: isOpen ? 'auto' : '52px',
              maxWidth: isOpen ? '192px' : '52px',
              maxHeight: '52px',
              transition: 'all 0.3s',
              objectFit: 'contain'
            }}
          />
        </div>

        <nav className="sidebar-nav">
          <Link
            to="/dashboard"
            className={`sidebar-link ${isActive('/dashboard') ? 'active' : ''}`}
            style={{
              background: isActive('/dashboard') ? colors.primary + '20' : 'transparent',
              color: isActive('/dashboard') ? colors.primary : colors.text,
            }}
          >
            <span className="sidebar-link-icon">📊</span>
            {isOpen && <span>Dashboard</span>}
          </Link>

          <Link
            to="/transfer"
            className={`sidebar-link ${isActive('/transfer') ? 'active' : ''}`}
            style={{
              background: isActive('/transfer') ? colors.primary + '20' : 'transparent',
              color: isActive('/transfer') ? colors.primary : colors.text,
            }}
          >
            <span className="sidebar-link-icon">💸</span>
            {isOpen && <span>Transfer $PIX</span>}
          </Link>

          <Link
            to="/export"
            className={`sidebar-link ${isActive('/export') ? 'active' : ''}`}
            style={{
              background: isActive('/export') ? colors.primary + '20' : 'transparent',
              color: isActive('/export') ? colors.primary : colors.text,
            }}
          >
            <span className="sidebar-link-icon">📦</span>
            {isOpen && <span>Download My Data</span>}
          </Link>

          <Link
            to="/library"
            className={`sidebar-link ${isActive('/library') ? 'active' : ''}`}
            style={{
              background: isActive('/library') ? colors.primary + '20' : 'transparent',
              color: isActive('/library') ? colors.primary : colors.text,
            }}
          >
            <span className="sidebar-link-icon">📚</span>
            {isOpen && <span>My Library</span>}
          </Link>

          <Link
            to="/profile"
            className={`sidebar-link ${isActive('/profile') ? 'active' : ''}`}
            style={{
              background: isActive('/profile') ? colors.primary + '20' : 'transparent',
              color: isActive('/profile') ? colors.primary : colors.text,
            }}
          >
            <span className="sidebar-link-icon">👤</span>
            {isOpen && <span>Profile</span>}
          </Link>
        </nav>

        <div className="sidebar-footer">
          <button
            onClick={handleLogout}
            className="sidebar-logout-btn"
            style={{
              background: 'transparent',
              color: colors.secondary,
              borderColor: colors.border
            }}
            title="Log Out"
          >
            {isOpen && <span>Log Out</span>}
          </button>
          <button
            onClick={() => setTheme(theme.mode === 'dark' ? 'light' : 'dark')}
            className="sidebar-theme-toggle"
            style={{
              background: colors.toggleInactive,
              color: colors.text,
              borderColor: colors.border
            }}
            title={theme.mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme.mode === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="sidebar-toggle-btn"
        style={{
          background: colors.card,
          borderColor: colors.border,
          color: colors.text
        }}
      >
        {isOpen ? '◀' : '▶'}
      </button>
    </>
  );
}

